// src/firebase/crossApp.js
// Cross-app single sign-on into ae-course-hosting — a separate app on its
// own Firebase project (see CLAUDE.md "Cross-app SSO"). Calls the
// mintCourseHostingToken Cloud Function to get a short-lived custom token
// for the caller's matching ae-course-hosting account (matched by email),
// then redirects with it in the URL hash — hash fragments never reach a
// server/log, and the token is single-use/short-lived regardless.
//
// Open to every role since 2026-09-08 (head marker, marker, and student) -
// still only works for someone who already has a matching-email account in
// ae-course-hosting; throws a friendly error otherwise (see
// mintCourseHostingToken).

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

// Staging builds point at staging ae-course-hosting (see .env.staging).
const AE_COURSE_HOSTING_URL = process.env.REACT_APP_AE_COURSE_HOSTING_URL || 'https://ae-course-hosting.web.app';

/**
 * Requests a sign-in token for ae-course-hosting and navigates there.
 * Throws with a friendly message if there's no matching account there.
 */
export const goToCourseHosting = async () => {
  const functions = getFunctions(getApp());
  const mintToken = httpsCallable(functions, 'mintCourseHostingToken');
  const { data } = await mintToken();
  window.location.href = `${AE_COURSE_HOSTING_URL}/#ssoToken=${encodeURIComponent(data.token)}`;
};

/**
 * Whether the signed-in user has a matching-email account in
 * ae-course-hosting, without minting a token - lets a dashboard hide the
 * "Course Content" button for someone who doesn't have one yet (e.g. a
 * student only ever set up on Homework Solutions), rather than showing it
 * and having goToCourseHosting fail on click. Fails closed (hides the
 * button) on any error, since this is a convenience nicety, not a feature
 * whose absence blocks anything.
 */
export const checkCourseHostingAccount = async () => {
  try {
    const functions = getFunctions(getApp());
    const check = httpsCallable(functions, 'hasCourseHostingAccount');
    const { data } = await check();
    return !!data.exists;
  } catch (error) {
    console.warn('Could not check ae-course-hosting account:', error);
    return false;
  }
};
